import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/primitives';
import { ErrorState, LoadingState } from '@/components/DataState';
import { getBlogDraft } from '@/data/sources-content';
import { BlogDraftView } from '@/voice/actions/BlogDraftView';

/* Dialogs reused by the Library console. The blog dialog is the SAME
 * BlogDraftView the Blog library opens (edit/save, copy text/HTML, repurpose,
 * WordPress push with its confirm step) — nothing new, nothing removed. */

export function BlogEditorDialog({ id, projectId, onClose, onChanged }: { id: number; projectId: number | null; onClose: () => void; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const draft = useQuery({ queryKey: ['content-blog-draft', id], queryFn: () => getBlogDraft(id), staleTime: 30_000 });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label="Saved blog draft" className="relative z-10 mt-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border-default bg-surface p-4 shadow-lg sm:mt-0">
        {draft.isLoading ? <LoadingState rows={4} /> : draft.isError ? (
          <div className="space-y-3">
            <ErrorState error={draft.error} onRetry={() => void draft.refetch()} />
            <button onClick={onClose} className="rounded-sm px-2 py-1.5 text-[11px] text-text-secondary hover:bg-hover hover:text-text-primary">Close</button>
          </div>
        ) : draft.data ? (
          <BlogDraftView
            draft={draft.data}
            onDismiss={onClose}
            projectId={projectId}
            onRepurpose={() => { void queryClient.invalidateQueries({ queryKey: ['social-content'] }); onChanged(); }}
            onSaved={(d) => { queryClient.setQueryData(['content-blog-draft', id], d); void queryClient.invalidateQueries({ queryKey: ['content-blog-drafts'] }); onChanged(); }}
          />
        ) : null}
      </div>
    </div>
  );
}

export function ConfirmDeleteDialog({ count, title, kind, onCancel, onConfirm, pending, error }: {
  count: number; title: string; kind: 'blog' | 'social' | 'mixed'; onCancel: () => void; onConfirm: () => void; pending: boolean; error: string | null;
}) {
  const body = count > 1
    ? `${count} items will be removed. Each is a soft delete (reversible) and is recorded for audit.`
    : kind === 'blog'
      ? 'will be removed from your library. This can be restored — it is a soft delete, and the action is recorded for audit.'
      : 'will be removed from your Studio. This is a soft delete (reversible) and is recorded for audit.';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => !pending && onCancel()} aria-hidden />
      <div role="alertdialog" aria-modal="true" aria-label="Delete" className="relative z-10 mt-16 w-full max-w-sm rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0">
        <header className="border-b border-border-subtle px-5 py-3"><h2 className="text-sm font-semibold text-text-primary">{count > 1 ? `Delete ${count} items?` : 'Delete this draft?'}</h2></header>
        <div className="space-y-3 px-5 py-4">
          <p className="text-sm text-text-secondary">{count > 1 ? body : <><span className="font-medium text-text-primary">{title || 'Untitled draft'}</span> {body}</>}</p>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
            <button onClick={onConfirm} disabled={pending} className="inline-flex h-control items-center justify-center rounded-md bg-danger px-4 text-sm font-medium text-white transition-colors hover:bg-danger/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 disabled:opacity-60">
              {pending ? 'Deleting…' : count > 1 ? `Delete ${count}` : 'Delete draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RequestChangesDialog({ count, onCancel, onConfirm, pending }: { count: number; onCancel: () => void; onConfirm: (note: string) => void; pending: boolean }) {
  const [note, setNote] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-scrim/40 backdrop-blur-[1px]" onClick={() => !pending && onCancel()} aria-hidden />
      <div role="dialog" aria-modal="true" aria-label="Request changes" className="relative z-10 mt-16 w-full max-w-md rounded-xl border border-border-default bg-surface shadow-lg sm:mt-0">
        <header className="border-b border-border-subtle px-5 py-3"><h2 className="text-sm font-semibold text-text-primary">Request changes{count > 1 ? ` on ${count} posts` : ''}</h2></header>
        <div className="space-y-3 px-5 py-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">What should change?</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={500}
              className="w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30" />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
            <Button variant="primary" onClick={() => onConfirm(note.trim())} disabled={pending || note.trim() === ''}>{pending ? 'Sending…' : 'Request changes'}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface BulkOutcome { key: string; title: string; ok: boolean; message: string }

export function BulkResults({ label, results, onClose }: { label: string; results: BulkOutcome[]; onClose: () => void }) {
  const done = results.filter((r) => r.ok).length;
  return (
    <div role="status" className="mt-3 rounded-md border border-border-subtle bg-surface p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-text-primary">{label}: {done} of {results.length} done{done < results.length ? `, ${results.length - done} not done` : ''}.</p>
        <button onClick={onClose} className="min-h-[36px] px-2 text-xs text-text-secondary hover:text-text-primary">Dismiss</button>
      </div>
      {results.some((r) => !r.ok) && (
        <ul className="mt-2 space-y-1 text-xs">
          {results.filter((r) => !r.ok).map((r) => <li key={r.key} className="text-danger">{r.title || 'Untitled'}: {r.message}</li>)}
        </ul>
      )}
    </div>
  );
}
